import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CadChatApi } from '../../contexts/CallContext';
import { CadApplyPolicy, useCadApplyPlan } from '../../hooks/useCadApplyPlan';
import {
  CadApplyResult,
  CadRecord,
  CallAttachment,
  CallDetails,
  isAppliedAutomatically,
  isCadRescoreInProgress,
} from '../../types/Call';
import { CadApplyDialog } from '../calldetails/CadApplyDialog';
import { CadRescoreDialog } from '../calldetails/CadRecordParts';
import { VerifiedResult } from '../calldetails/CadVerificationList';
import {
  APPLY_NOTHING_ALLOWED,
  APPLY_SETTINGS_LOADING,
  CadApplyOutcome,
  RESCORE_READINESS_NOTE,
  canRescoreCadSkills,
  describeAppliedFields,
  describeRescore,
  formatDateTime,
  reportApplyResult,
  rescoreButtonLabel,
} from '../calldetails/cadRecordDisplay';
import { CadPollState } from '../../hooks/useCadRecord';

/**
 * What a CAD check message shows: resolved by the pane from the record's live state, for the
 * record that is on the call right now.
 */
export interface CadCheckView {
  attachmentId: string;
  fileName: string;
  /** Why there is no check result to show, when the record was read but not checked. */
  checkFailed?: string;
  /** "Not now", or an apply from this message: the action bar goes away, the findings stay. */
  dismissed: boolean;
}

interface CadCheckMessageProps {
  check: CadCheckView;
  cadChat: CadChatApi;
  callDetails: CallDetails;
  onDismiss: () => void;
  /** An apply from this message finished; the pane posts the confirmation. */
  onApplied: (outcome: CadApplyOutcome) => void;
}

const CHAT_TEXT = { fontSize: '0.8rem' } as const;

/**
 * The assistant's rendering of a CAD check: the same findings, timing, apply and CAD Skills
 * rescore as the CAD Record tab, read from the same hook, laid out for a narrow pane. The action
 * bar offers the dialogs the tab opens.
 */
export const CadCheckMessage: React.FC<CadCheckMessageProps> = ({
  check,
  cadChat,
  callDetails,
  onDismiss,
  onApplied,
}) => {
  const { cad, canEdit } = cadChat;
  const { record, fields, fieldsError, verification, loadFields } = cad;
  const rootRef = useRef<HTMLDivElement>(null);

  // "Not now" and a finished apply both take the action bar away, with the button that had focus
  // in it; focus moves to the message so a keyboard user is not dropped to the page body.
  const focusMessage = () => requestAnimationFrame(() => rootRef.current?.focus());
  const handleDismiss = () => {
    onDismiss();
    focusMessage();
  };
  const handleApplied = (outcome: CadApplyOutcome) => {
    onApplied(outcome);
    focusMessage();
  };

  // Fields are loaded once per extraction by whichever view asks first (an audited read the hook
  // de-duplicates); after a failed load only the tab's "Load again" asks again.
  useEffect(() => {
    if (!fieldsError) loadFields();
  }, [fieldsError, loadFields]);

  const result = check.checkFailed ? undefined : verification;
  const eventId = fields?.eventId?.trim();

  return (
    <Stack ref={rootRef} tabIndex={-1} spacing={1.5} sx={{ ...CHAT_TEXT, outline: 'none' }}>
      <Typography variant="body2" sx={{ ...CHAT_TEXT, lineHeight: 1.6 }}>
        I read the CAD event report{eventId ? ` for ${eventId}` : ` ${check.fileName}`}
        {result ? ' and compared it with the transcript.' : '.'}
      </Typography>

      {check.checkFailed && (
        <Typography variant="body2" color="text.secondary" sx={CHAT_TEXT}>
          {check.checkFailed}
        </Typography>
      )}

      {result && record && <VerifiedResult record={record} result={result} dense />}

      {canEdit && !check.dismissed && record && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.25 }}>
          <ActionBar
            callDetails={callDetails}
            record={record}
            fields={fields}
            fieldsError={fieldsError}
            rescorePollState={cad.rescorePollState}
            onApply={cad.apply}
            onRescore={cad.requestRescore}
            onDismiss={handleDismiss}
            onApplied={handleApplied}
          />
        </Box>
      )}
    </Stack>
  );
};

const PENDING_HEADING: Record<CadApplyPolicy, string> = {
  // FillEmpty selects only rows the call record has no value for; Overwrite selects every row
  // whose CAD value differs, including ones the record already holds another value for.
  FillEmpty: 'Details the call record does not have yet',
  Overwrite: 'Details that differ from the call record',
};

/**
 * What the supervisor can do next. Owns the apply plan and the dialog, as the tab's apply button
 * owns its dialog: the plan resolved here is the one the dialog opens with, so the two agree on
 * "N fields" and the settings are read once per message.
 */
const ActionBar: React.FC<{
  callDetails: CallDetails;
  record: CallAttachment;
  fields: CadRecord | undefined;
  fieldsError: boolean;
  rescorePollState: CadPollState;
  onApply: CadChatApi['cad']['apply'];
  onRescore: CadChatApi['cad']['requestRescore'];
  onDismiss: () => void;
  onApplied: (outcome: CadApplyOutcome) => void;
}> = ({
  callDetails,
  record,
  fields,
  fieldsError,
  rescorePollState,
  onApply,
  onRescore,
  onDismiss,
  onApplied,
}) => {
  const plan = useCadApplyPlan(callDetails, fields, true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [rescoreOpen, setRescoreOpen] = useState(false);
  const [requestingRescore, setRequestingRescore] = useState(false);
  const reviewButtonRef = useRef<HTMLButtonElement>(null);
  const rescoreButtonRef = useRef<HTMLButtonElement>(null);
  const notNowButtonRef = useRef<HTMLButtonElement>(null);

  // On success the rescore button gives way to the wait's status line, and the review button is
  // absent when the call record already holds every allowed value; "Not now" is always there, so
  // focus never drops to the page body.
  const closeRescore = () => {
    setRescoreOpen(false);
    requestAnimationFrame(() =>
      (rescoreButtonRef.current ?? reviewButtonRef.current ?? notNowButtonRef.current)?.focus()
    );
  };
  const handleRescoreConfirmed = async () => {
    setRequestingRescore(true);
    await onRescore();
    setRequestingRescore(false);
    closeRescore();
  };

  // Cancel returns focus to the button that opened the dialog; a finished apply unmounts this
  // bar, and the parent moves focus to the message.
  const closeApply = () => {
    setApplyOpen(false);
    requestAnimationFrame(() => reviewButtonRef.current?.focus());
  };
  const handleApplied = (result: CadApplyResult) => {
    setApplyOpen(false);
    onApplied(reportApplyResult(result));
  };

  const caption = (text: string) => (
    <Typography variant="caption" color="text.secondary" display="block">
      {text}
    </Typography>
  );

  // The apply half of the bar depends on the fields and the agency's apply settings; the rescore
  // half and "Not now" do not, and stay whatever state the apply is in (an agency that allows no
  // field from a document can still rescore).
  const applyState = (() => {
    if (fieldsError) {
      return caption('The extracted fields could not be loaded. Open the CAD Record tab and choose Load again.');
    }
    if (!fields || plan.status === 'loading') {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={14} aria-hidden />
          {caption(fields ? APPLY_SETTINGS_LOADING : 'Loading the extracted fields…')}
        </Box>
      );
    }
    if (plan.status === 'error') return caption(plan.message);
    if (plan.rows.length === 0) return caption(APPLY_NOTHING_ALLOWED);
    return null;
  })();

  const rows = applyState === null && plan.status === 'ready' ? plan.rows : [];
  const pending = rows.filter((row) => row.preselected);
  const offerable = rows.filter((row) => row.blockedReason === null);
  const count = pending.length;
  const appliedAutomatically = isAppliedAutomatically(record) ? record.appliedAt : undefined;
  const canRescore = canRescoreCadSkills(callDetails);
  const rescoreRunning = isCadRescoreInProgress(record.rescoreStatus);
  const rescoreLine = describeRescore(record, rescorePollState);

  return (
    <Stack spacing={1}>
      {appliedAutomatically && (
        <Typography variant="body2" sx={CHAT_TEXT}>
          Applied to the call record automatically on {formatDateTime(appliedAutomatically)}
          {record.appliedFields && record.appliedFields.length > 0
            ? `: ${describeAppliedFields(record.appliedFields)}.`
            : '.'}
        </Typography>
      )}
      {count > 0 && plan.status === 'ready' && rows.length > 0 && (
        <Box>
          <Typography variant="body2" sx={{ ...CHAT_TEXT, fontWeight: 600 }}>
            {PENDING_HEADING[plan.policy]}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {pending.map((row) => (
              <Typography
                key={row.id}
                component="li"
                variant="body2"
                sx={{ ...CHAT_TEXT, overflowWrap: 'anywhere' }}
              >
                {row.label}: <strong>{row.cadValue}</strong>
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {applyState ??
        caption(
          offerable.length > 0
            ? 'The CAD file is saved on this call. What next? You review each change before it is saved. Updating the CAD Skills score rescores that category against the record.'
            : 'The CAD file is saved on this call. The call record already has every value the agency allows from it. Updating the CAD Skills score rescores that category against the record.'
        )}
      {/* The rescore's state, from the same row the tab reads; the Analysis tab shows the scores. */}
      {rescoreLine && caption(rescoreLine)}
      {/* Rather than a disabled control that cannot explain itself: the button is absent until the
          call has an analysis to update, and this line says why. */}
      {!canRescore && caption(RESCORE_READINESS_NOTE)}
      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
        {offerable.length > 0 && (
          <Button
            ref={reviewButtonRef}
            variant="contained"
            size="small"
            onClick={() => setApplyOpen(true)}
          >
            {count > 0
              ? `Review and save ${count} ${count === 1 ? 'field' : 'fields'}`
              : 'Review call record fields'}
          </Button>
        )}
        {canRescore && !rescoreRunning && (
          <Button
            ref={rescoreButtonRef}
            variant="outlined"
            size="small"
            onClick={() => setRescoreOpen(true)}
          >
            {rescoreButtonLabel(record)}
          </Button>
        )}
        <Button ref={notNowButtonRef} size="small" color="inherit" onClick={onDismiss}>
          Not now
        </Button>
      </Box>

      {rescoreOpen && (
        <CadRescoreDialog
          submitting={requestingRescore}
          onCancel={closeRescore}
          onConfirm={() => void handleRescoreConfirmed()}
        />
      )}

      {applyOpen && fields && (
        <CadApplyDialog
          callDetails={callDetails}
          fields={fields}
          plan={plan}
          onClose={closeApply}
          onApply={onApply}
          onApplied={handleApplied}
        />
      )}
    </Stack>
  );
};
